/**
 * Villagers: people walking the real streets around the player.
 *
 * Every vector chunk contributes its roads as kerbside lanes (see
 * `core/character/footpaths.ts`). The manager keeps a fixed number of people
 * alive within a radius of the player — spawning on lanes nearby, retiring
 * anyone who falls far behind — so the population follows the player through
 * the world without ever simulating a town that nobody is looking at.
 *
 * How many there are follows the place: the population is a ceiling, and
 * the share of it out and about follows how much walkable way there is
 * nearby. A town centre full of footways gets them all, and a village street
 * a few. A lane through the fields gets one or two. People start mostly on
 * footways, paths and pedestrian streets, sometimes on a quiet street, and
 * hardly ever at the kerb of a main road (see `core/character/footpaths.ts`).
 *
 * Behaviour is deliberately small: walk the lane, stop for a moment now and
 * then, turn round at the end, give the player a little room. Driven into,
 * they are knocked down as bodies are (`fallen.ts`), lie a moment, get up,
 * walk back to their path and hurry off. Most stroll, a
 * few jog, a few stand about. That is enough to make a street feel inhabited,
 * and it is cheap enough to run for every villager every frame.
 */

import type { Object3D } from 'three';
import { DODGE_SPEED, REACTION, SIDESTEP_SPEED, brace, dodgeSide, flinches } from '../../core/character/dodge.js';
import { advancePhase, gaitPose } from '../../core/character/gait.js';
import type { Footpath, PathLink, PathSample, RoadSpan } from '../../core/character/footpaths.js';
import { nearestAlong, sampleFootpath, spanAt } from '../../core/character/footpaths.js';
import type { Hit, Striker } from '../../core/character/struck.js';
import { KNOCKED_DOWN, strike } from '../../core/character/struck.js';
import type { OncomingCar, StreetPerson } from '../../core/traffic/pedestrians.js';
import { oncoming, safeToCross } from '../../core/traffic/pedestrians.js';
import { makeRng } from '../../core/build/rng.js';
import type { CharacterCrowd, CharacterLook } from './crowd.js';
import { lookFromSeed } from './crowd.js';
import type { FallStart } from '../../core/character/ragdoll.js';
import { startFall } from '../../core/character/ragdoll.js';
import type { BodyWorld } from './fallen.js';
import { Fallen } from './fallen.js';

interface ChunkPaths {
  group: Object3D;
  paths: Footpath[];
  /** Local-frame bounds of all its paths: minX, minZ, maxX, maxZ. */
  bounds: [number, number, number, number];
}

/** Every walkable lane currently streamed in, keyed by vector chunk. */
export class FootpathNetwork {
  private readonly chunks = new Map<string, ChunkPaths>();

  get chunkCount(): number {
    return this.chunks.size;
  }

  add(id: string, group: Object3D, paths: Footpath[]): void {
    if (paths.length === 0) {
      this.chunks.delete(id);
      return;
    }
    const bounds: [number, number, number, number] = [Infinity, Infinity, -Infinity, -Infinity];
    for (const path of paths) {
      for (let i = 0; i < path.points.length; i += 3) {
        bounds[0] = Math.min(bounds[0], path.points[i]);
        bounds[1] = Math.min(bounds[1], path.points[i + 2]);
        bounds[2] = Math.max(bounds[2], path.points[i]);
        bounds[3] = Math.max(bounds[3], path.points[i + 2]);
      }
    }
    this.chunks.set(id, { group, paths, bounds });
  }

  remove(id: string): void {
    this.chunks.delete(id);
  }

  get(id: string): ChunkPaths | undefined {
    return this.chunks.get(id);
  }

  /**
   * A random spot on a lane between `minRadius` and `maxRadius` of (x, z),
   * in scene coordinates.
   */
  pick(
    x: number,
    z: number,
    minRadius: number,
    maxRadius: number,
    rng: () => number,
  ): { chunk: string; path: number; distance: number } | undefined {
    const candidates: Array<{ chunk: string; path: number; distance: number }> = [];
    const weights: number[] = [];
    let total = 0;
    for (const [id, chunk] of this.chunks) {
      const lx = x - chunk.group.position.x;
      const lz = z - chunk.group.position.z;
      const [minX, minZ, maxX, maxZ] = chunk.bounds;
      if (lx < minX - maxRadius || lx > maxX + maxRadius || lz < minZ - maxRadius || lz > maxZ + maxRadius) {
        continue;
      }
      chunk.paths.forEach((path, index) => {
        // Not a street that has a pavement beside it.
        if (path.weight <= 0) return;
        // Every fourth point is plenty: lanes are resampled to ~10 m.
        for (let i = 0; i < path.distances.length; i += 4) {
          const d = Math.hypot(path.points[i * 3] - lx, path.points[i * 3 + 2] - lz);
          if (d >= minRadius && d <= maxRadius) {
            candidates.push({ chunk: id, path: index, distance: path.distances[i] });
            total += path.weight;
            weights.push(total);
          }
        }
      });
    }
    if (candidates.length === 0) return undefined;
    // Weighted: a footway is picked far more often than a main road's kerb.
    const u = rng() * total;
    let lo = 0, hi = weights.length - 1;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (weights[mid] < u) lo = mid + 1;
      else hi = mid;
    }
    return candidates[lo];
  }

  /**
   * Metres of walkable way within `radius` of scene (x, z), each weighted by
   * how much people walk it. A town centre has several kilometres of it, and
   * open country next to none.
   */
  walkableNear(x: number, z: number, radius: number): number {
    let sum = 0;
    for (const chunk of this.chunks.values()) {
      const lx = x - chunk.group.position.x;
      const lz = z - chunk.group.position.z;
      const [minX, minZ, maxX, maxZ] = chunk.bounds;
      if (lx < minX - radius || lx > maxX + radius || lz < minZ - radius || lz > maxZ + radius) continue;
      for (const path of chunk.paths) {
        for (let i = 1; i < path.distances.length; i++) {
          if (Math.hypot(path.points[i * 3] - lx, path.points[i * 3 + 2] - lz) > radius) continue;
          sum += (path.distances[i] - path.distances[i - 1]) * path.weight;
        }
      }
    }
    return sum;
  }
}

type Temperament = 'stroller' | 'jogger' | 'loiterer';

interface Villager {
  slot: number;
  chunk: string;
  path: number;
  distance: number;
  direction: 1 | -1;
  /** Cruising speed for this person, m/s. */
  pace: number;
  speed: number;
  pause: number;
  phase: number;
  yaw: number;
  /** Sideways offset from the lane, so two people never share a line. */
  lateral: number;
  /** The offset this person drifts back to once nobody is in the way. */
  home: number;
  /** Last drawn position, for the others to steer around. */
  x: number;
  y: number;
  z: number;
  clockOffset: number;
  temperament: Temperament;
  /** The road this person has looked at and is crossing, once it is clear. */
  cleared: RoadSpan | undefined;
  /** Seconds waiting at the kerb for a gap. */
  kerbWait: number;
  /** Seconds left of standing well over at the kerb's edge for a car going by. */
  giving: number;
  /** Seconds left of jumping out of the player's way. */
  dodging: number;
  /** How long they have seen the player coming at them, seconds; -1 when nothing is. */
  alarm: number;
  /** How long this person takes to see something coming and move, seconds. */
  react: number;
  /** Not looking: on the phone, lost in thought. They never see a car coming. */
  heedless: boolean;
  /** How far into a flinch they are, 0..1. */
  flinch: number;
  /** How fast they are moving across their lane this frame, m/s, positive toward `lateral`. */
  latSpeed: number;
  /** Metres to walk before turning off onto another path again. */
  hopCooldown: number;
  /** Where they were drawn before a turn onto another path, against where they are, faded out. */
  glideX: number;
  glideZ: number;
  /** Where they were on their path last frame, glide aside, for how fast they walk. */
  bx: number;
  bz: number;
  /** As the traffic sees them. */
  person: StreetPerson;
  /** Knocked down: the body as it tumbles, lies and gets up, and where its chunk's origin was last frame. */
  fallen?: Fallen;
  fallenOrigin?: { x: number; z: number };
  /** Up again and off their path: walking back onto it, to this far along it. */
  back?: number;
  /** Seconds left of hurrying, shaken, after being knocked down. */
  shaken: number;
  /**
   * A resident (see `addResident`): kept to this stretch of their path,
   * metres along it, turning back at either end and at the kerb, and not
   * counted among the crowd the place calls for.
   */
  leash?: { from: number; to: number };
}

/** A car as the villagers see it. */
export interface StreetCar {
  x: number;
  y: number;
  z: number;
  dx: number;
  dz: number;
  speed: number;
  halfLength: number;
  halfWidth: number;
}

/** Something the villagers can see the traffic through. */
export interface StreetTraffic {
  around(x: number, z: number, radius: number): StreetCar[];
}

export interface VillagerOptions {
  /** How many people to keep around the player. */
  population: number;
  /** Spawn no closer than this, once the initial crowd exists. */
  spawnMin: number;
  spawnMax: number;
  /** Retire anyone further than this. */
  despawn: number;
}

export const DEFAULT_VILLAGER_OPTIONS: VillagerOptions = {
  population: 14,
  spawnMin: 35,
  spawnMax: 150,
  despawn: 190,
};

const sample: PathSample = { x: 0, y: 0, z: 0, dx: 0, dz: 1 };
const kerbAt: PathSample = { x: 0, y: 0, z: 0, dx: 0, dz: 1 };
const meeting: OncomingCar = { distance: 0, speed: 0, onLine: false };

/** How far round the player the walkable ways are measured, metres. */
const SURVEY_RADIUS = 160;
/** Weighted metres of walkable way within the survey radius that call for everyone. */
const BUSY = 3500;
/** How far back from the road people wait at the kerb, metres. */
const KERB = 1;
/** Waiting longer than this for a gap, people give up and go another way, seconds. */
const GIVE_UP = 45;
/** How fast people cross a road, at the least, and when a car is coming, m/s. */
const CROSSING_PACE = 1.4;
const HURRY = 2.4;
/** The share of people who never see a car coming: on the phone, lost in thought. */
const HEEDLESS = 0.25;
/** How long someone knocked down hurries on for once back on their path, seconds. */
const SHAKEN = 12;

/**
 * The share of the population a place calls for, from its weighted metres of
 * walkable way: all of it in a town centre, a tenth or so on a village street,
 * and nobody where there is no way at all.
 */
export function populationShare(walkable: number): number {
  if (walkable < 30) return 0;
  return Math.min(1, Math.max(0.08, walkable / BUSY));
}

function wrapAngle(a: number): number {
  return Math.atan2(Math.sin(a), Math.cos(a));
}

export class Villagers {
  private readonly people: Villager[] = [];
  private readonly rng = makeRng(0x71a9e5);
  private seed = 1;
  private clock = 0;
  private populated = false;
  private opts: VillagerOptions;
  /** How much of the population this place calls for, 0..1. See `walkableNear`. */
  private share = 1;
  private sinceSurvey = Infinity;
  private traffic: StreetTraffic | undefined;
  /** The cars about, this frame. */
  private cars: StreetCar[] = [];
  /** The player's velocity, from how they moved between frames, m/s. */
  private playerVX = 0;
  private playerVZ = 0;
  private lastPX = NaN;
  private lastPZ = NaN;
  /** Everyone, as the traffic sees them: see `core/traffic/pedestrians.ts`. */
  readonly street: StreetPerson[] = [];
  /** Roads crossed, all told, and of those at mapped crossings. */
  crossed = 0;
  crossedAtZebras = 0;
  /** People knocked down, all told. */
  knockedDown = 0;
  /** The world as a body meets it, for anyone knocked down: see `setBodies`. */
  private bodies: BodyWorld | undefined;

  constructor(
    private readonly crowd: CharacterCrowd,
    private readonly network: FootpathNetwork,
    options: Partial<VillagerOptions> = {},
  ) {
    this.opts = { ...DEFAULT_VILLAGER_OPTIONS, ...options };
  }

  get count(): number {
    return this.people.length;
  }

  /** How many are out and about, residents aside: what the place's share is counted against. */
  private get crowdCount(): number {
    return this.people.reduce((n, v) => n + (v.leash === undefined ? 1 : 0), 0);
  }

  /** The ground and what is solid, for bodies knocked down to fall on and against. Until set, nobody can be. */
  setBodies(world: BodyWorld): void {
    this.bodies = world;
  }

  /**
   * Someone who stays about a place, dressed as `look`: an officer by their
   * station. On a path within `reach` metres of scene (x, z), kept to
   * `leash` metres either way along it from where they start, mostly
   * standing about. Returns their crowd slot, or undefined if there is no
   * path near or no room in the crowd.
   */
  addResident(look: CharacterLook, x: number, z: number, reach = 40, leash = 12): number | undefined {
    const spot = this.network.pick(x, z, 0, reach, this.rng);
    if (spot === undefined) return undefined;
    const chunk = this.network.get(spot.chunk);
    if (chunk === undefined || spanAt(chunk.paths[spot.path], spot.distance) !== undefined) return undefined;
    const slot = this.crowd.add(look);
    if (slot === undefined) return undefined;
    this.people.push(this.person(slot, spot, this.rng() < 0.6 ? 'loiterer' : 'stroller', { from: spot.distance - leash, to: spot.distance + leash }));
    return slot;
  }

  /**
   * Someone getting out of a car at scene (x, y, z), facing `yaw`, shaken:
   * they hurry to the nearest path and off along it. Returns their crowd
   * slot, or undefined if there is no path near or no room in the crowd.
   */
  addLeaving(look: CharacterLook, x: number, y: number, z: number, yaw: number): number | undefined {
    const spot = this.network.pick(x, z, 0, 30, this.rng);
    if (spot === undefined) return undefined;
    const slot = this.crowd.add(look);
    if (slot === undefined) return undefined;
    const v = this.person(slot, spot, 'stroller');
    v.x = x; v.y = y; v.z = z; v.yaw = yaw;
    v.back = spot.distance;
    v.shaken = SHAKEN;
    this.people.push(v);
    return slot;
  }

  /** Whether the resident in crowd slot `slot` is still about. */
  hasResident(slot: number): boolean {
    return this.people.some(v => v.slot === slot && v.leash !== undefined);
  }

  /** Send the resident in crowd slot `slot` away. */
  removeResident(slot: number): void {
    const i = this.people.findIndex(v => v.slot === slot && v.leash !== undefined);
    if (i >= 0) this.retire(i);
  }

  /** How many are down or getting back to their path. */
  get down(): number {
    return this.people.reduce((n, v) => n + (v.fallen !== undefined || v.back !== undefined ? 1 : 0), 0);
  }

  /**
   * A vehicle going by: anyone it strikes closing faster than
   * `KNOCKED_DOWN` is knocked down, thrown by their share of the hit; the
   * bumper, `bumper` metres up, takes their legs. Returns the hits, for
   * the vehicle to feel. Only people within a step of `y`, its base.
   */
  strike(vehicle: Striker, y: number, bumper: number): Hit[] {
    const hits: Hit[] = [];
    if (this.bodies === undefined) return hits;
    for (const v of this.people) {
      if (v.fallen !== undefined || !Number.isFinite(v.x) || Math.abs(v.y - y) > 1.5) continue;
      const hit = strike(vehicle, v.x, v.z, v.person.vx, v.person.vz);
      if (hit === undefined || hit.closing < KNOCKED_DOWN) continue;
      this.knockDown(v, { kind: 'struck', v: [hit.person.x, 0, hit.person.z], bumper });
      hits.push(hit);
    }
    return hits;
  }

  /** Down, from the pose they were last drawn in, set going as `fall` says. */
  private knockDown(v: Villager, fall: FallStart): void {
    const fallen = new Fallen(this.crowd, v.slot);
    startFall(fallen.body, fall);
    v.fallen = fallen;
    v.fallenOrigin = undefined;
    v.back = undefined;
    v.cleared = undefined;
    v.kerbWait = 0;
    v.pause = 0;
    v.dodging = 0;
    v.alarm = -1;
    v.flinch = 0;
    v.latSpeed = 0;
    v.speed = 0;
    this.knockedDown++;
  }

  /**
   * Off their path: down, tumbling, lying and getting up, then walking back
   * onto it, quickly, from wherever they came to rest.
   */
  private offPath(v: Villager, path: Footpath, origin: Object3D, dt: number): void {
    const ox = origin.position.x, oz = origin.position.z;
    const p = v.person;
    p.aimX = NaN;
    p.aimZ = NaN;
    const fallen = v.fallen;
    if (fallen !== undefined) {
      // The scene's origin moves now and then, the chunk with it, and the body with the chunk.
      if (v.fallenOrigin !== undefined) fallen.shift(ox - v.fallenOrigin.x, oz - v.fallenOrigin.z);
      v.fallenOrigin = { x: ox, z: oz };
      fallen.step(dt, this.bodies!, this.clock + v.clockOffset);
      const [x, y, z] = fallen.at;
      if (fallen.stood === undefined) {
        v.x = p.x = x;
        v.z = p.z = z;
        v.y = p.y = y;
        p.vx = p.vz = 0;
        fallen.draw();
        return;
      }
      const up = fallen.stood;
      v.fallen = undefined;
      v.fallenOrigin = undefined;
      v.x = up.x;
      v.y = up.y;
      v.z = up.z;
      v.yaw = up.yaw;
      v.back = nearestAlong(path, v.x - ox, v.z - oz).distance;
      v.speed = 0;
      v.shaken = SHAKEN;
    }
    sampleFootpath(path, v.back!, sample);
    const tx = sample.x + ox + sample.dz * v.lateral, tz = sample.z + oz - sample.dx * v.lateral;
    const dx = tx - v.x, dz = tz - v.z, d = Math.hypot(dx, dz);
    v.speed += Math.min(dt * 3.2, HURRY - v.speed);
    const step = Math.min(d, v.speed * dt);
    if (d > 1e-3) {
      v.x += (dx / d) * step;
      v.z += (dz / d) * step;
      v.yaw += wrapAngle(Math.atan2(-dx, -dz) - v.yaw) * Math.min(1, dt * 6);
    }
    v.y = this.bodies?.ground(v.x, v.z, v.y) ?? sample.y + origin.position.y;
    v.phase = advancePhase(v.phase, v.speed, dt);
    p.x = v.x; p.y = v.y; p.z = v.z;
    p.vx = d > 1e-3 ? (dx / d) * v.speed : 0;
    p.vz = d > 1e-3 ? (dz / d) * v.speed : 0;
    this.crowd.place(v.slot, { x: v.x, y: v.y, z: v.z, yaw: v.yaw, pose: gaitPose(v.speed, v.phase, this.clock + v.clockOffset), visible: true });
    if (d < 0.3) {
      // Back on the path: on along it the way they face.
      v.distance = v.back!;
      v.back = undefined;
      v.direction = sample.dx * Math.sin(-v.yaw) + sample.dz * -Math.cos(v.yaw) >= 0 ? 1 : -1;
      v.bx = NaN;
      v.glideX = v.glideZ = 0;
    }
  }

  /** The traffic, to look out for at the kerb and step aside from. */
  setTraffic(traffic: StreetTraffic): void {
    this.traffic = traffic;
  }

  /**
   * How many people should be about: the full population by day, a handful at
   * night. Surplus villagers are retired furthest-first, so nobody vanishes in
   * front of the player.
   */
  setPopulation(target: number): void {
    this.opts.population = Math.max(0, Math.round(target));
  }

  /** Advance everyone by `dt` and pose them. `(px, pz)` is the player, in scene metres. */
  update(dt: number, px: number, pz: number): void {
    this.clock += dt;
    this.sinceSurvey += dt;
    if (Number.isFinite(this.lastPX) && dt > 0) {
      // A teleport or a rebase is not a speed.
      const vx = (px - this.lastPX) / dt, vz = (pz - this.lastPZ) / dt;
      const ok = Math.hypot(vx, vz) < 80;
      const k = Math.min(1, dt * 6);
      this.playerVX += ((ok ? vx : 0) - this.playerVX) * k;
      this.playerVZ += ((ok ? vz : 0) - this.playerVZ) * k;
    }
    this.lastPX = px;
    this.lastPZ = pz;
    if (this.sinceSurvey > 2) {
      this.sinceSurvey = 0;
      this.share = populationShare(this.network.walkableNear(px, pz, SURVEY_RADIUS));
    }
    this.cars = this.traffic?.around(px, pz, this.opts.despawn + 40) ?? [];
    this.spawn(px, pz);
    if (this.crowdCount > this.wanted + 1) {
      let far = 0, farD = -1;
      this.people.forEach((v, i) => {
        const d = Math.hypot(v.x - px, v.z - pz);
        if (v.leash === undefined && Number.isFinite(d) && d > farD) { farD = d; far = i; }
      });
      if (farD > 40) this.retire(far);
    }

    this.street.length = 0;
    for (let i = this.people.length - 1; i >= 0; i--) {
      const v = this.people[i];
      let chunk = this.network.get(v.chunk);
      let path = chunk?.paths[v.path];
      if (chunk === undefined || path === undefined) {
        this.retire(i);
        continue;
      }

      sampleFootpath(path, v.distance, sample);
      const toPlayer = Math.hypot(sample.x + chunk.group.position.x - px, sample.z + chunk.group.position.z - pz);
      // A resident is sent away by whoever placed them (`removeResident`), not by distance.
      if (toPlayer > this.opts.despawn && v.leash === undefined) {
        this.retire(i);
        continue;
      }
      if (v.fallen !== undefined || v.back !== undefined) {
        this.offPath(v, path, chunk.group, dt);
        this.street.push(v.person);
        continue;
      }

      const look = this.think(v, path, chunk.group, dt);
      // A turn onto another path: carry on from there.
      chunk = this.network.get(v.chunk)!;
      path = chunk.paths[v.path];
      sampleFootpath(path, v.distance, sample);
      const x = sample.x + chunk.group.position.x;
      const z = sample.z + chunk.group.position.z;
      this.steer(v, path, x, z, sample.dx * v.direction, sample.dz * v.direction, px, pz, dt);

      // Face the way they are going, turning at a human rate rather than
      // snapping round at the end of a street: down the lane, or, stepping
      // across it, that way too; at the kerb, looking both ways. The legs
      // step out how fast they really go over the ground, across the lane
      // as well as along it, so nobody slides.
      const dx = sample.dx * v.direction;
      const dz = sample.dz * v.direction;
      // Across the lane, toward positive `lateral`: (dz, -dx) of the way it is drawn.
      const gx = dx * v.speed + sample.dz * v.latSpeed, gz = dz * v.speed - sample.dx * v.latSpeed;
      const going = Math.hypot(gx, gz);
      const want = going > 0.35 && Math.abs(v.latSpeed) > 0.3 ? Math.atan2(-gx, -gz) : Math.atan2(-dx, -dz) + look;
      v.yaw += wrapAngle(want - v.yaw) * Math.min(1, dt * (v.dodging > 0 ? 12 : 5));

      v.phase = advancePhase(v.phase, going, dt);
      const fade = Math.exp(-dt * 2.5);
      v.glideX *= fade;
      v.glideZ *= fade;
      const nx = x + sample.dz * v.lateral + v.glideX;
      const nz = z - sample.dx * v.lateral + v.glideZ;
      const p = v.person;
      // How they walk, not the glide over onto a new path: from where they
      // are on their path, skipping the frame they turn onto another.
      const bx = nx - v.glideX, bz = nz - v.glideZ;
      if (Number.isFinite(v.bx) && dt > 0 && Math.hypot(bx - v.bx, bz - v.bz) < 1) {
        const k = Math.min(1, dt * 8);
        p.vx += ((bx - v.bx) / dt - p.vx) * k;
        p.vz += ((bz - v.bz) / dt - p.vz) * k;
      }
      v.bx = bx;
      v.bz = bz;
      v.x = nx;
      v.z = nz;
      v.y = sample.y + chunk.group.position.y;
      p.x = v.x;
      p.y = v.y;
      p.z = v.z;
      this.street.push(p);
      this.crowd.place(v.slot, {
        x: v.x,
        y: sample.y,
        z: v.z,
        yaw: v.yaw,
        pose: brace(gaitPose(going, v.phase, this.clock + v.clockOffset), v.flinch),
        visible: true,
      });
    }
  }

  /**
   * Keep out of everyone's way by moving across the lane, not by stopping.
   *
   * Anyone — the player or another villager — within a metre and a half pushes
   * this person sideways, harder the closer they are and hardest when they are
   * dead ahead. Stopping is what made a crowd pile up round a player standing
   * on a pavement; stepping aside is what people do. The player standing in
   * the way is walked round, wide. The player running at someone makes them
   * jump aside. Walking a street with no pavement, a car coming by sends them
   * over to its edge.
   */
  private steer(v: Villager, path: Footpath, x: number, z: number, dx: number, dz: number, px: number, pz: number, dt: number): void {
    // Across the lane, positive to the side `lateral` measures toward: (dz, -dx)
    // of the way the path is drawn, whichever way they walk it.
    const ax = dz * v.direction, az = -dx * v.direction;
    let push = 0;
    const avoid = (ox: number, oz: number, weight: number, radius: number) => {
      const rx = ox - x, rz = oz - z;
      const d = Math.hypot(rx, rz);
      if (d > radius || d < 1e-4) return;
      const ahead = rx * dx + rz * dz;
      const side = rx * ax + rz * az - v.lateral;
      const urgency = (radius - d) / radius * (ahead > 0 ? 1.6 : 0.6) * weight;
      // Step to whichever side they are not on.
      push -= (side >= 0 ? 1 : -1) * urgency;
    };
    // The player on foot is walked round; going faster than anyone walks, in
    // a car, they are not a person to step round but something to get out
    // of the way of, or not.
    if (Math.hypot(this.playerVX, this.playerVZ) < 3) avoid(px, pz, 1.4, 2.2);
    for (const other of this.people) if (other !== v) avoid(other.x, other.z, 1, 1.6);

    // The player running or driving at them: where the player will pass
    // closest, and how soon. Close and soon, once they have seen it coming,
    // they jump the other way; too late for that, they flinch.
    const rx = v.x - px, rz = v.z - pz;
    const jump = v.heedless ? 0 : dodgeSide(rx, rz, this.playerVX, this.playerVZ, ax, az, v.slot % 2 === 0 ? 1 : -1);
    if (jump === 0) v.alarm = -1;
    else if (v.dodging <= 0) {
      v.alarm = Math.max(0, v.alarm) + dt;
      if (v.alarm >= v.react) {
        v.dodging = 0.9;
        v.home = jump * 1.5;
        v.alarm = -1;
      }
    }
    v.dodging = Math.max(0, v.dodging - dt);
    const flinch = flinches(rx, rz, this.playerVX, this.playerVZ) && (v.heedless ? v.react < 0.4 : v.alarm >= 0 || v.dodging > 0);
    v.flinch += ((flinch ? 1 : 0) - v.flinch) * Math.min(1, dt * (flinch ? 10 : 2.5));

    // A car coming by a street with no pavement: over to the kerb's edge.
    if (path.outward !== 0 && this.carPassing(v)) v.giving = 1.5;
    v.giving = Math.max(0, v.giving - dt);
    const reach = v.dodging > 0 ? 2.2 : 1.4;
    let target = Math.max(-reach, Math.min(reach, v.home + push * 1.4));
    if (v.giving > 0) target = path.outward * 1.3;
    if (v.dodging <= 0 && Math.abs(v.home) > 0.45) v.home *= Math.exp(-dt);
    // Over toward it, no faster than legs carry anyone sideways: drawn
    // walking or running that way (see `update`), never sliding. Leaping
    // aside, or standing over for a car, they turn and step across; anything
    // else (back to their line after a dodge, round someone in the way) they
    // drift over while walking on, a little off straight, and standing they
    // hardly drift at all.
    const was = v.lateral;
    const most = (v.dodging > 0 ? DODGE_SPEED : v.giving > 0 ? SIDESTEP_SPEED : Math.max(0.12, 0.4 * v.speed)) * dt;
    v.lateral += Math.max(-most, Math.min(most, (target - v.lateral) * Math.min(1, dt * (v.dodging > 0 ? 9 : 3))));
    v.latSpeed = dt > 0 ? (v.lateral - was) / dt : 0;
  }

  /**
   * Whether a car is coming by close enough that someone at the kerb should
   * stand over, or is standing behind them, waiting to get by.
   */
  private carPassing(v: Villager): boolean {
    for (const car of this.cars) {
      if (Math.abs(car.y - v.y) > 3) continue;
      const rx = v.x - car.x, rz = v.z - car.z;
      if (Math.abs(rx) > 32 || Math.abs(rz) > 32) continue;
      const along = rx * car.dx + rz * car.dz;
      if (along < -car.halfLength - 2 || along > 30) continue;
      if (Math.abs(rx * car.dz - rz * car.dx) < car.halfWidth + 2.4) return true;
    }
    return false;
  }

  /**
   * Walk on, and decide what to do: stop for a moment now and then, turn
   * round or onto another path at the end of this one, now and then turn off
   * onto a path that meets it, and at a road, wait at the kerb, look both
   * ways and cross when there is a gap. Returns how far they are looking
   * away from the way they walk, radians.
   */
  private think(v: Villager, path: Footpath, origin: Object3D, dt: number): number {
    let look = 0;
    const inRoad = spanAt(path, v.distance);
    // Found in the road (just arrived, or turned onto it): get across.
    if (inRoad !== undefined && v.cleared === undefined) v.cleared = inRoad;
    if (inRoad === undefined && v.cleared !== undefined &&
      (v.distance < v.cleared.start - KERB - 0.5 || v.distance > v.cleared.end + KERB + 0.5)) {
      const across = v.direction === 1 ? v.distance > v.cleared.end : v.distance < v.cleared.start;
      if (across) {
        this.crossed++;
        if (v.cleared.zebra) this.crossedAtZebras++;
      }
      v.cleared = undefined;
    }

    let target = v.pace;
    let stop = NaN;
    v.person.aimX = NaN;
    v.person.aimZ = NaN;
    const span = inRoad === undefined ? this.spanAhead(path, v) : undefined;
    // A resident turns back at either end of their stretch, and at the kerb.
    const leash = v.leash;
    if (leash !== undefined && v.pause <= 0) {
      const kerb = span === undefined ? Infinity : ((v.direction === 1 ? span.start - KERB : span.end + KERB) - v.distance) * v.direction;
      if ((v.direction === 1 ? v.distance >= leash.to : v.distance <= leash.from) || kerb < 1.5) this.turnRound(v);
    }
    if (span !== undefined && leash === undefined) {
      const kerb = v.direction === 1 ? span.start - KERB : span.end + KERB;
      const toKerb = (kerb - v.distance) * v.direction;
      const beyond = v.direction === 1 ? span.end >= path.length - 0.5 : span.start <= 0.5;
      if (toKerb < 5 && beyond && !this.endLinked(path, v.direction)) {
        // The road runs out with the path, and nowhere to go on: no crossing.
        stop = kerb;
        if (toKerb < 0.3) this.turnRound(v);
      } else if (toKerb < 5) {
        stop = kerb;
        if (toKerb < 0.4) {
          v.pause = 0;
          v.kerbWait += dt;
          // Looking left, then right, then left again.
          look = Math.sin(v.kerbWait * 2.1 + v.clockOffset) * 0.9;
          sampleFootpath(path, kerb, kerbAt);
          const kx = kerbAt.x + origin.position.x, kz = kerbAt.z + origin.position.z;
          const wx = kerbAt.dx * v.direction, wz = kerbAt.dz * v.direction;
          if (span.zebra) {
            // Waiting at a crossing: drivers must stop for them.
            v.person.aimX = kx + wx * (KERB + 0.8);
            v.person.aimZ = kz + wz * (KERB + 0.8);
          }
          if (this.clearToCross(v, span, kx, kz, wx, wz)) {
            v.cleared = span;
            v.kerbWait = 0;
            stop = NaN;
          } else if (v.kerbWait > GIVE_UP) {
            v.kerbWait = 0;
            this.turnRound(v);
          }
        }
      }
    }

    if (inRoad !== undefined) {
      // On the road: across at a steady pace, quicker with a car coming.
      v.pause = 0;
      target = Math.max(v.pace, this.carNear(v, 22) ? HURRY : CROSSING_PACE);
    } else if (v.pause > 0) {
      // Not standing about in front of a car waiting to get by.
      if (this.carNear(v, 10, true)) v.pause = Math.min(v.pause, 0.5);
      v.pause -= dt;
      if (v.pause <= 0 && this.rng() < 0.35 && Number.isNaN(stop)) v.direction = v.direction === 1 ? -1 : 1;
    } else {
      const before = v.distance;
      v.distance += v.direction * v.speed * dt;
      v.hopCooldown -= v.speed * dt;
      if (!Number.isNaN(stop) && (v.distance - stop) * v.direction > 0) v.distance = stop;
      if (v.distance <= 0 || v.distance >= path.length) {
        v.distance = Math.min(path.length, Math.max(0, v.distance));
        if (!this.hop(v, path, origin, true, before)) this.turnRound(v);
      } else if (v.hopCooldown <= 0 && Number.isNaN(stop) && this.hop(v, path, origin, false, before)) {
        // Turned off onto another path.
      } else if (Number.isNaN(stop) && span === undefined && this.rng() < dt * (v.temperament === 'loiterer' ? 0.25 : v.temperament === 'jogger' ? 0.01 : 0.04)) {
        // A glance in a shop window, a chat over the fence; never in the road.
        v.pause = v.temperament === 'loiterer' ? 4 + this.rng() * 9 : 1.5 + this.rng() * 4;
      }
    }
    if (inRoad !== undefined) {
      v.distance += v.direction * v.speed * dt;
      v.distance = Math.min(path.length, Math.max(0, v.distance));
      if (v.distance <= 0 || v.distance >= path.length) {
        if (!this.hop(v, path, origin, true, v.distance)) this.turnRound(v);
      }
    }

    // Leaping aside, or flinching, nobody walks on.
    if (v.dodging > 0 || v.flinch > 0.5) target = 0;
    // Shaken, after being knocked down: hurrying off, no stopping to look about.
    if (v.shaken > 0) {
      v.shaken -= dt;
      v.pause = 0;
      target = Math.max(target, HURRY);
    }
    const waitingHere = !Number.isNaN(stop) && Math.abs(stop - v.distance) < 0.4;
    if (v.pause > 0 || waitingHere) target = 0;
    // People accelerate and brake over about half a second, which is also
    // what lets the gait blend in rather than snap.
    const rate = target > v.speed ? 3.2 : 5;
    v.speed += Math.max(-rate * dt, Math.min(rate * dt, target - v.speed));
    // Slowing to the kerb rather than walking past it and stepping back.
    if (!Number.isNaN(stop)) v.speed = Math.min(v.speed, Math.max(0, Math.abs(stop - v.distance)) * 2.5 + 0.05);
    return look;
  }

  private turnRound(v: Villager): void {
    v.direction = v.direction === 1 ? -1 : 1;
    v.pause = 0.6 + this.rng() * 2.5;
    v.kerbWait = 0;
  }

  /** The next stretch of road ahead on this path, the way they walk it. */
  private spanAhead(path: Footpath, v: Villager): RoadSpan | undefined {
    if (v.direction === 1) {
      for (const span of path.spans) if (span.end > v.distance) return span === v.cleared ? undefined : span;
    } else {
      for (let i = path.spans.length - 1; i >= 0; i--) {
        const span = path.spans[i];
        if (span.start < v.distance) return span === v.cleared ? undefined : span;
      }
    }
    return undefined;
  }

  /** Whether the end they are walking toward meets another path. */
  private endLinked(path: Footpath, direction: 1 | -1): boolean {
    const end = direction === 1 ? path.length : 0;
    return path.links.some(l => Math.abs(l.at - end) < 1);
  }

  /** Whether a car is coming within `range` metres of them, or with `standing`, waiting there. */
  private carNear(v: Villager, range: number, standing = false): boolean {
    for (const car of this.cars) {
      if ((standing ? car.speed > 1 : car.speed < 0.5) || Math.abs(car.y - v.y) > 3) continue;
      const rx = v.x - car.x, rz = v.z - car.z;
      const along = rx * car.dx + rz * car.dz;
      if (along > -car.halfLength && along < range && Math.abs(rx * car.dz - rz * car.dx) < car.halfWidth + 2) return true;
    }
    return false;
  }

  /**
   * Looking both ways from the kerb at scene (kx, kz), to cross along (wx, wz):
   * whether every car coming leaves a gap (see `safeToCross`).
   */
  private clearToCross(v: Villager, span: RoadSpan, kx: number, kz: number, wx: number, wz: number): boolean {
    // A moment at the kerb to look, first.
    if (v.kerbWait < 0.8) return false;
    const width = span.end - span.start + KERB * 2;
    const coming: OncomingCar[] = [];
    for (const car of this.cars) {
      if (Math.abs(car.y - v.y) > 3 || Math.abs(car.x - kx) > 95 || Math.abs(car.z - kz) > 95) continue;
      if (oncoming(car, kx, kz, wx, wz, width, meeting) !== undefined) coming.push({ ...meeting });
    }
    return safeToCross(width, Math.max(v.pace, CROSSING_PACE), span.zebra, coming);
  }

  /**
   * Turn onto another path where this one meets it: at its end (`atEnd`),
   * most likely, and passing one that meets it partway, now and then, more
   * often one that leads over a road. `before` is where they were along
   * this path a frame ago. True if they turned.
   */
  private hop(v: Villager, path: Footpath, origin: Object3D, atEnd: boolean, before: number): boolean {
    if (v.leash !== undefined) return false;
    const chunk = this.network.get(v.chunk);
    if (chunk === undefined || path.links.length === 0) return false;
    let link: PathLink | undefined;
    if (atEnd) {
      const end = v.distance <= 0 ? 0 : path.length;
      const options = path.links.filter(l => Math.abs(l.at - end) < 1 && (chunk.paths[l.path]?.weight ?? 0) > 0);
      if (options.length === 0 || this.rng() > 0.85) return false;
      link = options[Math.floor(this.rng() * options.length)];
    } else {
      const lo = Math.min(before, v.distance), hi = Math.max(before, v.distance);
      for (const l of path.links) {
        if (l.at <= lo || l.at > hi || l.at < 1 || l.at > path.length - 1) continue;
        const onto = chunk.paths[l.path];
        // Never off the pavement into a street that has one (weight 0).
        if (onto !== undefined && onto.weight > 0 && this.rng() < (onto.spans.length > 0 ? 0.5 : 0.2)) link = l;
        break;
      }
      if (link === undefined) return false;
    }
    const onto = chunk.paths[link.path];
    if (onto === undefined) return false;
    // Drawn where they were, and eased over onto the new path.
    sampleFootpath(onto, link.to, kerbAt);
    const fromX = v.x, fromZ = v.z;
    const heading = { x: sample.dx * v.direction, z: sample.dz * v.direction };
    v.path = link.path;
    v.distance = link.to;
    v.direction = link.to < 1 ? 1 : link.to > onto.length - 1 ? -1
      : kerbAt.dx * heading.x + kerbAt.dz * heading.z >= 0 ? 1 : -1;
    v.hopCooldown = 4;
    v.cleared = undefined;
    v.kerbWait = 0;
    if (Number.isFinite(fromX)) {
      v.glideX = fromX - (kerbAt.x + origin.position.x + kerbAt.dz * v.lateral);
      v.glideZ = fromZ - (kerbAt.z + origin.position.z - kerbAt.dx * v.lateral);
      if (Math.hypot(v.glideX, v.glideZ) > 5) v.glideX = v.glideZ = 0;
    }
    return true;
  }

  /** How many people should be about here and now. */
  private get wanted(): number {
    return Math.round(this.opts.population * this.share);
  }

  private spawn(px: number, pz: number): void {
    // The first fill may put people right next to the player — there is no
    // one to see them appear. After that, new arrivals come from out of sight.
    const min = this.populated ? this.opts.spawnMin : 6;
    let budget = this.populated ? 2 : this.wanted;
    while (this.crowdCount < this.wanted && budget-- > 0) {
      const spot = this.network.pick(px, pz, min, this.opts.spawnMax, this.rng);
      if (spot === undefined) return;
      // Never spawn on top of someone, nor in the road, nor beside a car.
      const chunk = this.network.get(spot.chunk);
      if (chunk !== undefined && spanAt(chunk.paths[spot.path], spot.distance) !== undefined) continue;
      const at = chunk !== undefined ? sampleFootpath(chunk.paths[spot.path], spot.distance, sample) : undefined;
      if (at !== undefined && chunk !== undefined) {
        const sx = at.x + chunk.group.position.x;
        const sz = at.z + chunk.group.position.z;
        if (Math.hypot(sx - px, sz - pz) < 3 || this.people.some(o => Math.hypot(o.x - sx, o.z - sz) < 3)) continue;
        if (this.cars.some(c => Math.hypot(c.x - sx, c.z - sz) < 15)) continue;
      }
      const seed = this.seed++;
      const slot = this.crowd.add(lookFromSeed(seed * 2654435761));
      if (slot === undefined) return;
      const roll = this.rng();
      const temperament: Temperament = roll < 0.12 ? 'jogger' : roll < 0.3 ? 'loiterer' : 'stroller';
      this.people.push(this.person(slot, spot, temperament));
    }
    if (this.people.length > 0) this.populated = true;
  }

  /** Someone new on `spot`, in crowd slot `slot`, of a temperament, and kept to `leash` if a resident. */
  private person(slot: number, spot: { chunk: string; path: number; distance: number }, temperament: Temperament, leash?: { from: number; to: number }): Villager {
    const pace = temperament === 'jogger'
      ? 3.4 + this.rng() * 1.8
      : temperament === 'loiterer' ? 0.9 + this.rng() * 0.3 : 1.15 + this.rng() * 0.45;
    return {
      slot,
      chunk: spot.chunk,
      path: spot.path,
      distance: spot.distance,
      direction: this.rng() < 0.5 ? 1 : -1,
      pace,
      speed: temperament === 'loiterer' ? 0 : pace,
      pause: temperament === 'loiterer' ? this.rng() * 6 : 0,
      phase: this.rng(),
      yaw: this.rng() * Math.PI * 2,
      lateral: 0,
      home: (this.rng() - 0.5) * 0.9,
      x: Infinity,
      y: 0,
      z: Infinity,
      clockOffset: this.rng() * 10,
      temperament,
      cleared: undefined,
      kerbWait: 0,
      giving: 0,
      dodging: 0,
      alarm: -1,
      react: REACTION[0] + this.rng() * (REACTION[1] - REACTION[0]),
      heedless: leash === undefined && this.rng() < HEEDLESS,
      flinch: 0,
      latSpeed: 0,
      hopCooldown: leash === undefined ? 0 : Infinity,
      glideX: 0,
      glideZ: 0,
      bx: NaN,
      bz: NaN,
      person: { x: 0, y: 0, z: 0, vx: 0, vz: 0, aimX: NaN, aimZ: NaN },
      shaken: 0,
      leash,
    };
  }

  private retire(index: number): void {
    this.crowd.remove(this.people[index].slot);
    this.people.splice(index, 1);
  }
}
